import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { type SidebarItem } from './sidebar';

/**
 * Please check the https://nextui.org/docs/guide/routing to have a seamless router integration
 */

const createAdminSectionItems = (tenantSlug: string): SidebarItem[] => [
  {
    key: 'admin',
    icon: 'solar:checklist-minimalistic-outline',
    title: 'Admin',
    items: [
      {
        key: 'agents',
        href: `/${tenantSlug}/agents`,
        title: 'Agents',
        icon: 'la:robot',
      },
      {
        key: 'learning-paths',
        href: `/${tenantSlug}/learning-paths`,
        title: 'Learning Paths',
        icon: 'ph:steps-bold',
      },
      {
        key: 'cohorts',
        href: `/${tenantSlug}/cohorts`,
        title: 'Cohorts',
        icon: 'lucide:users',
      },
      {
        key: 'user-profiles',
        href: `/${tenantSlug}/user-profiles`,
        icon: 'hugeicons:profile',
        title: 'User Profiles',
      },
      {
        key: 'practice-history',
        href: `/${tenantSlug}/practice-history`,
        icon: 'material-symbols:work-history-outline',
        title: 'Practice History',
      },
      {
        key: 'users',
        href: `/${tenantSlug}/users`,
        title: 'User Management',
        icon: 'ci:users-group',
      },
      {
        key: 'variables',
        icon: 'hugeicons:variable',
        title: 'Variables',
        href: `/${tenantSlug}/variables/`,
      },
      {
        key: 'ai-providers',
        href: `/${tenantSlug}/ai-providers`,
        icon: 'material-symbols:api',
        title: 'AI Providers',
      },
      {
        key: 'evaluations',
        href: `/${tenantSlug}/evaluations`,
        icon: 'material-symbols:balance',
        title: 'Evaluations',
      },
      {
        key: 'tenant-config',
        href: `/${tenantSlug}/tenant-config`,
        icon: 'heroicons:cog-6-tooth',
        title: 'Tenant Config',
      },
    ],
  },
];

const createSectionItems = (tenantSlug: string): SidebarItem[] => [
  {
    key: 'menu',
    icon: 'solar:checklist-minimalistic-outline',
    title: 'Menu',
    items: [
      {
        key: 'dashboard',
        href: `/${tenantSlug}/home`,
        icon: 'material-symbols:dashboard-outline',
        title: 'Dashboard',
      },
      {
        key: 'leadership-gym',
        href: `/${tenantSlug}/leadership-gym`,
        icon: 'hugeicons:equipment-gym-03',
        title: 'Leadership Gym',
      },
      {
        key: 'campaigns',
        href: `/${tenantSlug}/campaigns`,
        icon: 'material-symbols:campaign-outline-sharp',
        title: 'Campaigns',
      },
      {
        key: 'lift-coach',
        href: `/${tenantSlug}/lift-coach`,
        icon: 'solar:users-group-two-rounded-outline',
        title: 'Lift Coach',
      },
      {
        key: 'documents',
        href: `/${tenantSlug}/documents`,
        icon: 'oui:documents',
        title: 'Documents',
      },
    ],
  },
];

export const createSidebarItems = (
  tenantSlug: string,
  isAdmin: boolean = true
) => {
  const baseItems = createSectionItems(tenantSlug);

  if (isAdmin) {
    return [...baseItems, ...createAdminSectionItems(tenantSlug)];
  }

  return baseItems;
};

// Hook to get sidebar items within a component
export const useSidebarItems = (isAdmin: boolean = true) => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return useMemo(
    () => createSidebarItems(tenantSlug || '', isAdmin),
    [tenantSlug, isAdmin]
  );
};

export const items: SidebarItem[] = [];

export const sectionItemsWithTeams: SidebarItem[] = [];
